import { useEffect, useRef, useState } from "react";
import { api, API } from "@/lib/api";
import {
  UploadSimple, FilePdf, Sparkle, Trash, ToggleLeft, ToggleRight,
  CheckCircle, Warning, FileText,
} from "@phosphor-icons/react";

const fmtEUR = (n) => {
  // Trunca a 2 decimales SIN redondear (788.249 → 788,24 €).
  const v = Math.trunc((Number(n) || 0) * 100) / 100;
  return new Intl.NumberFormat("es-ES", {
    style: "currency",
    currency: "EUR",
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(v);
};

/**
 * Tab de aseguradora: módulos personalizados de prexistencias.
 * El admin sube un PDF/imagen/XLSX con el listado oficial de la compañía,
 * la IA lo parsea y se persisten los items. Toggle para activar uso propio
 * vs. CYPE estándar.
 */
export default function ModulosTab({ aseguradoraId, canEdit, token }) {
  const [data, setData] = useState({ items: [], usar_modulos_propios: false, source_filename: null, extracted_at: null, status: null, error: null });
  const [uploading, setUploading] = useState(false);
  const [err, setErr] = useState("");
  const [confirmDelete, setConfirmDelete] = useState(false);
  const fileRef = useRef(null);
  const pollRef = useRef(null);

  const load = async () => {
    try {
      const r = await api.get(`/aseguradoras/${aseguradoraId}/modulos`);
      setData(r.data);
      // Refleja en el banner el error persistido en BD (p.ej. "saldo agotado")
      if (r.data && (r.data.status === "error" || r.data.status === "empty")) {
        setErr(r.data.error || "Error al procesar el documento");
      } else if (r.data && r.data.status === "ok") {
        setErr("");
      }
      return r.data;
    } catch (e) {
      setErr(e?.response?.data?.detail || "Error cargando módulos");
      return null;
    }
  };

  // Polling automático mientras la IA procesa
  useEffect(() => {
    load();
    return () => clearInterval(pollRef.current);
    // eslint-disable-next-line
  }, [aseguradoraId]);

  useEffect(() => {
    clearInterval(pollRef.current);
    if (data.status === "processing") {
      pollRef.current = setInterval(async () => {
        const d = await load();
        if (d && d.status !== "processing") {
          clearInterval(pollRef.current);
          setUploading(false);
          if (d.status === "error" || d.status === "empty") {
            setErr(d.error || "Error al procesar el documento");
          } else if (d.status === "ok") {
            setErr("");
          }
        }
      }, 3000);
    }
    return () => clearInterval(pollRef.current);
  }, [data.status]);

  const onPick = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true); setErr("");
    try {
      const fd = new FormData();
      fd.append("file", file);
      await api.post(`/aseguradoras/${aseguradoraId}/modulos/upload`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      // Tras la subida, el backend procesa en background; el polling se activa
      load();
    } catch (e2) {
      setErr(e2?.response?.data?.detail || "No se pudo procesar el archivo");
      setUploading(false);
    } finally {
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const reprocess = async () => {
    setUploading(true); setErr("");
    try {
      await api.post(`/aseguradoras/${aseguradoraId}/modulos/reprocess`);
      load();
    } catch (e) {
      setErr(e?.response?.data?.detail || "No se pudo reprocesar");
      setUploading(false);
    }
  };

  const toggle = async () => {
    const next = !data.usar_modulos_propios;
    try {
      await api.put(`/aseguradoras/${aseguradoraId}/modulos/toggle`, { usar_modulos_propios: next });
      load();
    } catch (e) {
      setErr(e?.response?.data?.detail || "Error");
    }
  };

  const removeAll = async () => {
    setConfirmDelete(false);
    try {
      await api.delete(`/aseguradoras/${aseguradoraId}/modulos`);
      load();
    } catch (e) {
      setErr(e?.response?.data?.detail || "Error eliminando módulos");
    }
  };

  const tieneItems = (data.items || []).length > 0;
  const tieneSourceFile = !!data.source_filename;
  const hasProvs = (data.items || []).some((it) => it.provincia_code || it.provincia_nombre);

  return (
    <div className="p-6 max-w-5xl space-y-5" data-testid="modulos-tab">
      {data.status === "processing" && (
        <div className="bg-amber-50 border border-amber-200 text-amber-900 text-xs px-3 py-2 rounded-md inline-flex items-start gap-2" data-testid="modulos-processing">
          <Sparkle className="w-3.5 h-3.5 mt-0.5 animate-pulse" weight="fill" />
          <div>
            <b>La IA está analizando el documento.</b> Esto puede tardar 1-2 minutos en
            documentos grandes con tablas por provincia. La pestaña se actualizará
            automáticamente al terminar.
          </div>
        </div>
      )}

      {err && (
        <div className="bg-status-error-bg border border-status-error-bd text-status-error-fg text-xs px-3 py-2 rounded-md inline-flex items-start gap-2">
          <Warning className="w-3.5 h-3.5 mt-0.5" weight="fill" />{err}
        </div>
      )}

      {/* Toggle de uso */}
      <section className="bg-white border border-border rounded-md p-5">
        <div className="flex items-start justify-between gap-4">
          <div className="flex-1 min-w-0">
            <h3 className="font-display font-bold text-sm tracking-tight">Módulos para Prexistencias</h3>
            <p className="text-xs text-muted mt-1">
              Por defecto, los expedientes usan los módulos <b>CYPE oficiales</b> según la provincia
              del riesgo. Si esta compañía exige sus propios módulos, súbelos aquí (PDF, imagen,
              Excel o CSV) y la IA los detectará automáticamente. Puedes alternar entre uno y otro.
            </p>
          </div>
          <button
            onClick={toggle}
            disabled={!canEdit || !tieneItems}
            data-testid="toggle-modulos-propios"
            title={!tieneItems ? "Sube primero un listado de módulos" : ""}
            className={`flex-shrink-0 inline-flex items-center gap-2 rounded-full px-3 py-1.5 text-xs font-semibold border transition ${
              data.usar_modulos_propios
                ? "bg-amber-500 text-white border-amber-500"
                : "bg-white text-slate-700 border-slate-300 hover:bg-slate-50"
            } disabled:opacity-50 disabled:cursor-not-allowed`}
          >
            {data.usar_modulos_propios ? (
              <><ToggleRight className="w-4 h-4" weight="fill" />Usar módulos de la compañía</>
            ) : (
              <><ToggleLeft className="w-4 h-4" weight="fill" />Usar CYPE (estándar)</>
            )}
          </button>
        </div>

        <div className="mt-3 grid grid-cols-2 gap-3">
          <SourceCard
            active={!data.usar_modulos_propios}
            title="Módulos CYPE"
            subtitle="Estándar oficial · 52 provincias"
            icon={CheckCircle}
            tone="emerald"
          />
          <SourceCard
            active={data.usar_modulos_propios && tieneItems}
            title="Módulos de la compañía"
            subtitle={tieneItems ? `${data.items.length} conceptos · IA detectó del documento` : "Sin documento subido"}
            icon={Sparkle}
            tone="amber"
          />
        </div>
      </section>

      {/* Subir documento */}
      <section className="bg-white border border-border rounded-md p-5">
        <div className="flex items-start justify-between gap-4 mb-3">
          <div className="flex-1 min-w-0">
            <h3 className="font-display font-bold text-sm tracking-tight">Listado oficial de la compañía</h3>
            <p className="text-xs text-muted mt-1">
              Sube el PDF (o imagen / Excel / CSV) que la aseguradora te haya facilitado.
              La IA leerá el documento y extraerá los conceptos con su precio €/m² (Básica / Media / Alta).
            </p>
          </div>
          {canEdit && (tieneItems || tieneSourceFile) && (
            <div className="flex items-center gap-2">
              <button
                onClick={reprocess}
                disabled={uploading || data.status === "processing"}
                data-testid="reprocess-modulos"
                title="Vuelve a leer el documento original con la IA (útil si la extracción anterior falló o quedó incompleta)"
                className="text-xs text-violet-700 hover:bg-violet-50 px-2 py-1 rounded border border-violet-200 inline-flex items-center gap-1 disabled:opacity-50"
              >
                <Sparkle className="w-3 h-3" weight="bold" />Reprocesar con IA
              </button>
              <button
                onClick={() => setConfirmDelete(true)}
                data-testid="delete-modulos"
                className="text-xs text-red-700 hover:bg-red-50 px-2 py-1 rounded border border-red-200 inline-flex items-center gap-1"
              >
                <Trash className="w-3 h-3" weight="bold" />Eliminar
              </button>
            </div>
          )}
        </div>

        {tieneItems ? (
          <div className="bg-emerald-50 border border-emerald-200 rounded-md p-3 inline-flex items-center gap-3 text-[12px]" data-testid="modulos-source-info">
            <FilePdf className="w-5 h-5 text-emerald-700" weight="fill" />
            <div>
              <div className="font-semibold text-emerald-900">{data.source_filename}</div>
              <div className="text-emerald-700">
                <CheckCircle className="w-3 h-3 inline mr-1" weight="fill" />
                {data.items.length} módulos detectados
                {data.extracted_at && <> · {new Date(data.extracted_at).toLocaleString("es-ES")}</>}
              </div>
            </div>
            <a
              href={`${API}/aseguradoras/${aseguradoraId}/modulos/source?token=${token}`}
              target="_blank"
              rel="noopener noreferrer"
              className="ml-3 text-emerald-700 hover:underline text-xs font-semibold"
            >
              Descargar original
            </a>
          </div>
        ) : tieneSourceFile ? (
          <div className="bg-amber-50 border border-amber-200 rounded-md p-3 inline-flex items-center gap-3 text-[12px]" data-testid="modulos-source-info-error">
            <FilePdf className="w-5 h-5 text-amber-700" weight="fill" />
            <div>
              <div className="font-semibold text-amber-900">{data.source_filename}</div>
              <div className="text-amber-800">
                <Warning className="w-3 h-3 inline mr-1" weight="fill" />
                Documento subido pero la IA aún no lo ha procesado correctamente. Pulsa “Reprocesar con IA”.
              </div>
            </div>
            <a
              href={`${API}/aseguradoras/${aseguradoraId}/modulos/source?token=${token}`}
              target="_blank"
              rel="noopener noreferrer"
              className="ml-3 text-amber-700 hover:underline text-xs font-semibold"
            >
              Descargar original
            </a>
          </div>
        ) : (
          <div className="text-[12px] text-muted">Sin documento. Sube uno para activar los módulos propios de esta compañía.</div>
        )}

        {canEdit && (
          <div className="mt-4">
            <input
              ref={fileRef}
              type="file"
              accept=".pdf,.png,.jpg,.jpeg,.webp,.xlsx,.xls,.csv"
              onChange={onPick}
              className="hidden"
              data-testid="modulos-file-input"
            />
            <button
              onClick={() => fileRef.current?.click()}
              disabled={uploading}
              data-testid="modulos-upload-btn"
              className="inline-flex items-center gap-2 bg-amber-500 hover:bg-amber-600 text-white text-sm font-semibold rounded-md px-4 py-2 disabled:opacity-50"
            >
              {uploading ? (
                <><Sparkle className="w-4 h-4 animate-pulse" weight="fill" />La IA está analizando el documento…</>
              ) : (
                <><UploadSimple className="w-4 h-4" weight="bold" />{tieneSourceFile ? "Reemplazar listado" : "Subir listado de módulos"}</>
              )}
            </button>
            <span className="ml-3 text-[11px] text-muted">PDF, imagen, Excel o CSV</span>
          </div>
        )}
      </section>

      {/* Tabla de módulos */}
      {tieneItems && (
        <section className="bg-white border border-border rounded-md overflow-hidden">
          <div className="px-4 py-2.5 border-b border-border bg-slate-50 flex items-center gap-2">
            <FileText className="w-4 h-4 text-muted" weight="bold" />
            <div className="font-semibold text-[13px]">Módulos detectados</div>
            {hasProvs && (
              <span className="text-[10px] uppercase tracking-widest font-bold text-amber-700 bg-amber-50 border border-amber-200 rounded-full px-1.5 py-0.5">
                Con variación por provincia
              </span>
            )}
            <span className="text-[11px] text-muted ml-auto">{data.items.length} entradas</span>
          </div>
          <table className="w-full text-sm">
            <thead className="bg-slate-50/50 text-[10px] uppercase tracking-widest text-muted">
              <tr>
                <th className="text-left py-2 px-4 font-semibold">Concepto</th>
                {hasProvs && <th className="text-left py-2 px-4 font-semibold w-32">Provincia</th>}
                <th className="text-left py-2 px-4 font-semibold w-44">Categoría</th>
                <th className="text-right py-2 px-4 font-semibold w-24">Básica</th>
                <th className="text-right py-2 px-4 font-semibold w-24">Media</th>
                <th className="text-right py-2 px-4 font-semibold w-24">Alta</th>
              </tr>
            </thead>
            <tbody>
              {data.items.map((it, idx) => (
                <tr key={it.id || `${it.concepto}-${it.provincia_code || "nac"}-${idx}`} className="border-t border-border/60 hover:bg-surface/40" data-testid={`modulo-row-${idx}`}>
                  <td className="py-2 px-4 font-medium">{it.concepto}</td>
                  {hasProvs && (
                    <td className="py-2 px-4 text-[12px]">
                      {it.provincia_code ? (
                        <span className="text-amber-800 font-semibold">{it.provincia_code} · {it.provincia_nombre}</span>
                      ) : it.provincia_nombre ? (
                        <span className="text-slate-500">{it.provincia_nombre}</span>
                      ) : (
                        <span className="text-slate-400">Nacional</span>
                      )}
                    </td>
                  )}
                  <td className="py-2 px-4 text-muted text-[12px]">{it.subcategoria || it.categoria || "—"}</td>
                  <td className="py-2 px-4 text-right tabular-nums">{fmtEUR(it.basica)}</td>
                  <td className="py-2 px-4 text-right tabular-nums">{fmtEUR(it.media)}</td>
                  <td className="py-2 px-4 text-right tabular-nums">{fmtEUR(it.alta)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </section>
      )}

      {confirmDelete && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50">
          <div className="bg-white rounded-md p-5 w-full max-w-sm shadow-lg">
            <h3 className="font-bold text-base mb-2">Eliminar módulos propios</h3>
            <p className="text-xs text-muted mb-4">Se borrarán los {data.items.length} módulos detectados y la aseguradora volverá a usar CYPE estándar. ¿Continuar?</p>
            <div className="flex justify-end gap-2">
              <button onClick={() => setConfirmDelete(false)} className="text-xs px-3 py-1.5 border border-border rounded-md">Cancelar</button>
              <button onClick={removeAll} data-testid="confirm-delete-modulos" className="text-xs px-3 py-1.5 bg-red-600 text-white rounded-md font-semibold">Eliminar</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function SourceCard({ active, title, subtitle, icon: Icon, tone }) {
  const ring = active
    ? tone === "amber"
      ? "border-amber-400 bg-amber-50/50"
      : "border-emerald-400 bg-emerald-50/50"
    : "border-border bg-white";
  const iconColor = active
    ? tone === "amber" ? "text-amber-600" : "text-emerald-600"
    : "text-slate-300";
  return (
    <div className={`border rounded-md p-3 transition ${ring}`}>
      <div className="flex items-center gap-2">
        <Icon className={`w-4 h-4 ${iconColor}`} weight="fill" />
        <div className="font-semibold text-[13px]">{title}</div>
        {active && <span className="ml-auto text-[10px] uppercase tracking-widest font-bold text-emerald-700">Activo</span>}
      </div>
      <div className="text-[11px] text-muted mt-1 ml-6">{subtitle}</div>
    </div>
  );
}
